export type TelegramNotificationType = "error" | "success" | "warning";

export interface TelegramWebAppLike {
  isVersionAtLeast?(version: string): boolean;
  ready?(): void;
  expand?(): void;
  enableClosingConfirmation?(): void;
  disableClosingConfirmation?(): void;
  setHeaderColor?(color: string): void;
  setBackgroundColor?(color: string): void;
  showConfirm?(message: string, callback: (confirmed: boolean) => void): void;
  showAlert?(message: string, callback?: () => void): void;
  HapticFeedback?: {
    notificationOccurred?(type: TelegramNotificationType): void;
  };
}

function safeInvoke(action: (() => void) | undefined): boolean {
  if (!action) return false;
  try {
    action();
    return true;
  } catch {
    return false;
  }
}

export function telegramSupports(webApp: TelegramWebAppLike | undefined, version: string): boolean {
  try {
    return webApp?.isVersionAtLeast?.(version) ?? false;
  } catch {
    return false;
  }
}

export function configureTelegramShell(webApp: TelegramWebAppLike | undefined, color = "#0b0d10"): void {
  safeInvoke(webApp?.ready ? () => webApp.ready?.() : undefined);
  safeInvoke(webApp?.expand ? () => webApp.expand?.() : undefined);
  if (telegramSupports(webApp, "6.1")) {
    safeInvoke(webApp?.setHeaderColor ? () => webApp.setHeaderColor?.(color) : undefined);
    safeInvoke(webApp?.setBackgroundColor ? () => webApp.setBackgroundColor?.(color) : undefined);
  }
  if (telegramSupports(webApp, "6.2")) {
    safeInvoke(webApp?.enableClosingConfirmation ? () => webApp.enableClosingConfirmation?.() : undefined);
  }
}

export function releaseTelegramShell(webApp: TelegramWebAppLike | undefined): void {
  if (!telegramSupports(webApp, "6.2")) return;
  safeInvoke(webApp?.disableClosingConfirmation ? () => webApp.disableClosingConfirmation?.() : undefined);
}

export function confirmTelegramAction(
  webApp: TelegramWebAppLike | undefined,
  message: string,
  browserConfirm: (message: string) => boolean,
): Promise<boolean> {
  if (telegramSupports(webApp, "6.2") && webApp?.showConfirm) {
    return new Promise((resolve) => {
      if (!safeInvoke(() => webApp.showConfirm?.(message, resolve))) resolve(browserConfirm(message));
    });
  }
  return Promise.resolve(browserConfirm(message));
}

export function notifyTelegramHaptic(
  webApp: TelegramWebAppLike | undefined,
  type: TelegramNotificationType,
): void {
  if (!telegramSupports(webApp, "6.1")) return;
  const notify = webApp?.HapticFeedback?.notificationOccurred;
  safeInvoke(notify ? () => notify.call(webApp.HapticFeedback, type) : undefined);
}

export function showTelegramAlert(
  webApp: TelegramWebAppLike | undefined,
  message: string,
  browserAlert: (message: string) => void,
): void {
  if (telegramSupports(webApp, "6.2") && webApp?.showAlert) {
    if (safeInvoke(() => webApp.showAlert?.(message))) return;
  }
  browserAlert(message);
}
